import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert } from '../components/auth/Alert.js';
import { OtpInput } from '../components/auth/OtpInput.js';
import { AuthLayout } from '../layouts/AuthLayout.js';
import { AUTH_STORAGE_KEYS, mfaVerifyRequest } from '../lib/auth-api.js';

type AlertState =
  | { kind: 'invalid_code' }
  | { kind: 'rate_limited'; retryAfterSeconds: number | null }
  | { kind: 'network_error'; message: string }
  | null;

export function MfaVerifyPage() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [otpResetKey, setOtpResetKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<AlertState>(null);
  const [expired, setExpired] = useState(false);

  // Mount-time guard: without a challenge_token the user can't be here.
  // Don't redirect inside render — that would cause a setState-in-render
  // warning. Defer to useEffect.
  useEffect(() => {
    const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.challengeToken);
    if (!token) navigate('/login');
  }, [navigate]);

  async function submit(code: string): Promise<void> {
    if (submitting) return;
    const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.challengeToken);
    if (!token) {
      navigate('/login');
      return;
    }
    setSubmitting(true);
    setAlert(null);
    const result = await mfaVerifyRequest(token, code);
    if (result.ok) {
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.challengeToken);
      navigate('/');
      return;
    }
    setSubmitting(false);
    if (result.kind === 'challenge_expired') {
      setExpired(true);
      return;
    }
    // Reset the OtpInput so the user can retype, and re-focus cell #1.
    setOtp('');
    setOtpResetKey((k) => k + 1);
    if (result.kind === 'invalid_code') {
      setAlert({ kind: 'invalid_code' });
      return;
    }
    if (result.kind === 'rate_limited') {
      setAlert({ kind: 'rate_limited', retryAfterSeconds: result.retryAfterSeconds });
      return;
    }
    setAlert({ kind: 'network_error', message: result.message });
  }

  if (expired) {
    return (
      <AuthLayout title="Session de connexion expirée">
        <Alert variant="error">
          Votre session de connexion a expiré. Reconnectez-vous pour reprendre la vérification.
        </Alert>
        <Link
          to="/login"
          className="btn-primary w-full mt-6"
          data-testid="mfa-verify-back-to-login"
        >
          Retour à la connexion
        </Link>
      </AuthLayout>
    );
  }

  const canSubmit = !submitting && otp.length === 6;

  return (
    <AuthLayout
      title="Vérification en deux étapes"
      subtitle="Saisissez le code à 6 chiffres généré par votre application d’authentification."
      belowCard={
        <p className="text-sm text-center">
          <Link to="/recovery-code" className="btn-ghost" data-testid="mfa-verify-recovery-link">
            Utiliser un code de récupération à la place
          </Link>
        </p>
      }
    >
      {alert ? (
        <div className="mb-4">
          <AlertView alert={alert} />
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) void submit(otp);
        }}
      >
        <OtpInput
          key={otpResetKey}
          ariaLabel="Code à 6 chiffres"
          disabled={submitting}
          onChange={(partial) => {
            setOtp(partial);
            // Clear the inline error as soon as the user starts typing
            // again — keeps the UI from feeling sticky.
            if (alert) setAlert(null);
          }}
          onComplete={(code) => {
            setOtp(code);
            void submit(code);
          }}
        />

        <button
          type="submit"
          disabled={!canSubmit}
          aria-busy={submitting || undefined}
          className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="mfa-verify-submit"
        >
          {submitting ? 'Vérification…' : 'Vérifier'}
        </button>
      </form>
    </AuthLayout>
  );
}

function AlertView({ alert }: { alert: NonNullable<AlertState> }) {
  if (alert.kind === 'invalid_code') {
    return (
      <Alert variant="error">
        Code incorrect. Vérifiez sur votre application d’authentification.
      </Alert>
    );
  }
  if (alert.kind === 'rate_limited') {
    const sec = alert.retryAfterSeconds;
    const body =
      sec === null
        ? 'Trop de tentatives. Patientez un instant avant de réessayer.'
        : `Trop de tentatives. Patientez environ ${Math.max(1, sec)} seconde${sec > 1 ? 's' : ''} avant de réessayer.`;
    return (
      <Alert variant="warning" title="Trop de tentatives">
        {body}
      </Alert>
    );
  }
  return <Alert variant="error">{alert.message}</Alert>;
}
